import React, { useEffect, useState } from 'react';
import character from '../../../../assets/img/character.svg';
import s from './SharedModal.module.css';
import Modal from '../modal/Modal';
import { usePostProject } from '../../hooks/usePostProject';
import { useSelector } from 'react-redux';
import {
    selectCurrentProject,
    selectIsLoadingSharing,
} from '../../../store/selectors/projectSelectors';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../button/Button';
import IconCheck from '../icons/IconCheck';
import Spinner from '../spinner/Spinner';
import { selectUserSubscription } from '../../../store/selectors/subscriptionSelector';

import { useAppDispatch } from '../../../store/store';
import { updateUserSubscription } from '../../../store/slices/subscriptionSlice';
import DropdownMenu from '../../ui/dropdown-menu/DropDownMenu';
import { selectSocialPages } from '../../../store/selectors/authSelectors';
import SelectPage from '../../../pages/landing-page/onboarding-page/SelectPage';
import { fetchSocialPages } from '../../api/socialApi';
import { updateProjectSocialPage } from '../../api/authApi';
import { toast } from 'react-toastify';

interface SharedModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PostModal: React.FC<SharedModalProps> = ({ isOpen, onClose }) => {
    const project = useSelector(selectCurrentProject);
    const platform = project?.content_type || 'platform';
    const [selectedSocialPage, setSelectedSocialPage] = useState<string>('');
    const { postProject, isPosted, setIsPosted } = usePostProject();
    const isLoadingShare = useSelector(selectIsLoadingSharing);
    const userSubscription = useSelector(selectUserSubscription);

    const navigate = useNavigate();
    const [successModal, setSuccessModal] = useState(false);
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [isLoadingSocialPages, setIsLoadingSocialPages] = useState(false);

    const handleClose = () => {
        setIsPosted(false);
        setSuccessModal(false);
        onClose();
    };

    const isPostDisabled =
        (platform === 'facebook' || platform === 'pinterest') && !selectedSocialPage;

    useEffect(() => {
        if (isOpen && isPosted) {
            setIsPosted(false);
            setSuccessModal(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isPosted) {
            dispatch(
                updateUserSubscription({
                    ...userSubscription,
                    direct_posts_used: (userSubscription?.direct_posts_used ?? 0) + 1,
                    remaining_posts: (userSubscription?.remaining_posts ?? 0) - 1,
                }),
            );
            if (location.pathname.includes('/ready-post')) {
                handleClose();
                navigate(`/published-post/${project?.id}`);
            } else {
                setSuccessModal(true);
            }
        }
    }, [isPosted]);

    useEffect(() => {
        if (platform) {
            setIsLoadingSocialPages(true);
            dispatch(fetchSocialPages(platform)).then(() => {
                setIsLoadingSocialPages(false);
            });
        }
    }, [dispatch, platform]);

    const handleChangeSocialPage = (option: { label: string; value: string }) => {
        setSelectedSocialPage(option.value);
        dispatch(
            updateProjectSocialPage({
                projectId: project?.id || '',
                socialPage: {
                    id: option.value,
                    type: 'social_pages',
                },
            }),
        )
            .unwrap()
            .then(() => {
                toast.success('Social page updated');
            })
            .catch((error) => {
                toast.error('Error updating social page');
            });
    };

    return (
        <Modal
            isOpen={isOpen || isPosted || isLoadingShare}
            onClose={handleClose}
            showHeader={false}
            width="400px"
        >
            <div className={s.post_modal}>
                {isLoadingShare && !successModal && (
                    <>
                        <p className={s.modal_title}>Posting to {platform}...</p>
                        <p className={s.success_text} style={{ marginBottom: '10px' }}>
                            Please wait while we publish your project.
                        </p>
                        <Spinner />
                    </>
                )}

                {!isLoadingShare && !isPosted && !successModal && (
                    <>
                        <p className={s.modal_title}>Ready to post to {platform}?</p>
                        {(platform === 'facebook' || platform === 'pinterest') && (
                            <div className={s.social_page_container}>
                                {isLoadingSocialPages ? (
                                    <Spinner />
                                ) : (
                                    <SelectPage
                                        selectedSocial={platform}
                                        onChange={(option) => handleChangeSocialPage(option)}
                                        source="post"
                                    />
                                )}
                            </div>
                        )}
                        <div className={s.post_modal_buttons}>
                            <Button
                                onClick={handleClose}
                                text="Cancel"
                                style={{
                                    background: 'white',
                                    color: '#23262A',
                                    border: '1px solid #E5E7EB',
                                }}
                            />

                            <Button
                                onClick={postProject}
                                text="Post Now"
                                state={isPostDisabled ? 'disabled' : 'enabled'}
                                style={{
                                    background: isPostDisabled ? '#E5E7EB' : '#FFDD35',
                                    color: isPostDisabled ? '#9CA3AF' : '#000',
                                }}
                            />
                        </div>
                    </>
                )}
                {successModal && (
                    <>
                        <div className={s.success_icon}>
                            <IconCheck />
                        </div>
                        <h2 className={s.modal_title}>Success! Post has been posted</h2>
                        <p className={s.success_text}>
                            You have successfully posted your post via SimpleCreate
                        </p>
                        <div className={s.button_container}>
                            <Button
                                onClick={handleClose}
                                text="Got it!"
                                style={{ background: '#FFDD35' }}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
